import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { Comment, PostCommentPayload, PutCommentPayload } from '../types/comment';

@Injectable({
  providedIn: 'root'
})
export class CommentService {
  private readonly apiUrl = environment.apiUrl;

  private http = inject(HttpClient);
  private toastr = inject(ToastrService);

  comments = signal<Comment[]>([]);
  caseId = signal<number | null>(null);
  isOpen = signal<boolean>(false);

  toggle(): void {
    this.isOpen.update(v => !v);
  }

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  getComments(caseId: number): Observable<Comment[]> {
    this.caseId.set(caseId);
    return this.http.get<Comment[]>(`${this.apiUrl}/rest/comment/get-comments/${caseId}`).pipe(
      tap((comments: Comment[]) => {
        this.comments.set(comments);
      }),
      catchError((error) => {
        this.toastr.error('Megjegyzések betöltése sikertelen');
        return throwError(() => error);
      })
    );
  }

  resetComments(): void {
    this.comments.set([]);
    this.caseId.set(null);
    this.isOpen.set(false);
  }

  postComment(payload: PostCommentPayload): Observable<Comment> {
    return this.http.post<Comment>(`${this.apiUrl}/rest/comment/post-comment`, payload).pipe(
      tap((comment: Comment) => {
        this.toastr.success('Megjegyzés sikeresen elküldve');
      }),
      catchError((error) => {
        this.toastr.error('Megjegyzés elküldése sikertelen');
        return throwError(() => error);
      })
    );
  }

  putComment(commentId: number, payload: PutCommentPayload): Observable<Comment> {
    return this.http.put<Comment>(`${this.apiUrl}/rest/comment/modify-comment/${commentId}`, payload).pipe(
      tap((comment: Comment) => {
        this.toastr.success('Megjegyzés sikeresen módosítva');
      }),
      catchError((error) => {
        this.toastr.error('Megjegyzés módosítása sikertelen');
        return throwError(() => error);
      })
    );
  }

  deleteComment(commentId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/rest/comment/delete-comment/${commentId}`).pipe(
      tap(() => {
        this.toastr.success('Megjegyzés sikeresen törölve');
      }),
      catchError((error) => {
        this.toastr.error('Megjegyzés törlése sikertelen');
        return throwError(() => error);
      })
    );
  }
}
